import { NewSubscriptionApi } from 'api/newSubscriptionApi';
import { PaymentTypes } from 'appConstants';
import FormGeneralError from 'components/formComponents/FormGeneralError';
import FormStripeCardField from 'components/formComponents/FormStripeCardField';
import FormTextField from 'components/formComponents/FormTextField';
import { useFormikContext } from 'formik';

import { useEffect, useState } from 'react';
import { formatNumber } from 'utils/formating';
import { UpgradePackageInfo, UpgradeSubscriptionFormValues } from './models';

type Props = {
  selectedPackage: UpgradePackageInfo;
  vatRate: number | null;
};

export const UpgradeSubscriptionsForm: React.FC<Props> = ({
  selectedPackage,
  vatRate,
}) => {
  const formikContext = useFormikContext<UpgradeSubscriptionFormValues>();

  const [promoCode, setPromoCode] = useState({ value: '', error: '' });

  const [priceInfo, setPriceInfo] = useState({ price: 0, vat: 0, total: 0 });

  const applyPromoCode = async () => {
    const result = await NewSubscriptionApi.checkPromoCode(promoCode.value);
    if (result.data) {
      const packages = result.data.generalPackages
        .map((i: any) => i.subjects.map((j: any) => j.subscriptions))
        .flat()
        .flat();

      const selectedPackage = packages.find(
        (p: any) =>
          p.packageID === formikContext.getFieldProps('package').value.packageId
      );

      const discountedTotalPriceWithoutVAT =
        selectedPackage.price - selectedPackage.promoCodeDiscount;

      const discountedTotalPriceWithVAT =
        discountedTotalPriceWithoutVAT +
        discountedTotalPriceWithoutVAT * (result.data.vatRate / 100);

      const discount = selectedPackage.price - discountedTotalPriceWithoutVAT;

      const vatValue =
        discountedTotalPriceWithVAT - discountedTotalPriceWithoutVAT;

      setPriceInfo({
        price: formatNumber(selectedPackage.price),
        vat: formatNumber(vatValue),
        total: formatNumber(discountedTotalPriceWithVAT),
      });

      formikContext.setFieldValue('validPromoCode', promoCode.value);
      formikContext.setFieldValue('validDiscount', discount);
      setPromoCode({
        value: '',
        error: '',
      });

      formikContext.setFieldValue('total', discountedTotalPriceWithVAT);
    }
    if (result.error) {
      setPromoCode({ value: '', error: result.error.message });
      formikContext.setFieldValue('validPromoCode', '');
      formikContext.setFieldValue('validDiscount', 0);
    }
  };

  const calculatePrice = () => {
    if (!vatRate) return;
    const price = selectedPackage.price;

    const vat = price * (vatRate / 100);

    const total = price + vat;

    setPriceInfo({ price, vat, total });
  };

  useEffect(() => {
    calculatePrice();
  }, [vatRate]);

  useEffect(() => {
    if (!priceInfo.total) return;
    formikContext.setFieldValue('total', priceInfo.total);
  }, [priceInfo.total]);

  return (
    <>
      <label className="SubscriptionOption Selected" style={{ cursor: 'none' }}>
        <div className="Left">
          <input
            type="radio"
            name="subscription-type"
            className="HiddenInput"
            checked={true}
            onChange={() => {}}
          />
          <label className="CustomInput" />
          <div className="Text">
            <h2>{selectedPackage.subscription}</h2>
            <p>{`Accessible for ${
              selectedPackage.type === 'Standard' ? 1 : 3
            } years`}</p>
          </div>
        </div>
        <h2>£{formatNumber(selectedPackage.price)}</h2>
      </label>
      <div className="UpgradePaymentType">
        <div
          className={`PaymentType ${
            formikContext.values.paymentType === PaymentTypes.PAY_BY_CARD
              ? 'Selected'
              : ''
          }`}
          onClick={() =>
            formikContext.setFieldValue('paymentType', PaymentTypes.PAY_BY_CARD)
          }
        >
          {PaymentTypes.PAY_BY_CARD}
        </div>
        <div
          className={`PaymentType ${
            formikContext.values.paymentType === PaymentTypes.PAY_BY_INVOICE
              ? 'Selected'
              : ''
          }`}
          onClick={() =>
            formikContext.setFieldValue(
              'paymentType',
              PaymentTypes.PAY_BY_INVOICE
            )
          }
        >
          {PaymentTypes.PAY_BY_INVOICE}
        </div>
      </div>
      <div className="DiscoundContainer">
        <input
          className="Discount"
          type="text"
          onChange={(e) =>
            setPromoCode((prevState) => ({
              ...prevState,
              value: e.target.value,
            }))
          }
          value={promoCode.value}
          placeholder="Enter promo code (optional)"
        />

        <button
          className="Btn BtnOrange"
          type="button"
          disabled={promoCode.value ? false : true}
          onClick={applyPromoCode}
        >
          Apply
        </button>
      </div>

      <FormGeneralError message={promoCode.error} />
      {formikContext.values.paymentType === PaymentTypes.PAY_BY_CARD && (
        <>
          <h4 className="SpaceTopBtm">Payment details </h4>
          <div className="CardPaymetFields">
            <FormTextField
              field={'cardholderName'}
              label={"Cardholder's name"}
              labelPosition={'left'}
              required={true}
            />

            <FormStripeCardField
              field={'card'}
              label={'Card '}
              required={true}
              labelPosition={'left'}
            />
          </div>
        </>
      )}

      <div
        className="TotalDetails"
        style={{ width: '100%', paddingTop: '30px' }}
      >
        <div className="DetailsItem">
          <p>Price</p>
          <span>£ {formatNumber(priceInfo.price)}</span>
        </div>
        {formikContext.getFieldProps('validDiscount').value > 0 && (
          <div className="DetailsItem">
            <p>Discount</p>
            <span>
              -£
              {formatNumber(formikContext.getFieldProps('validDiscount').value)}
            </span>
          </div>
        )}
        <div className="DetailsItem">
          <p>VAT ({vatRate}%)</p>
          <span>£ {formatNumber(priceInfo.vat)}</span>
        </div>
        <div className="DetailsItem">
          <p>Total price</p>
          <span>
            £ {formatNumber(formikContext.getFieldProps('total').value)}
          </span>
        </div>
      </div>
    </>
  );
};
